import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { portalConst } from '@app/config';
import { AlertService, NotificationService, StudentService } from '@app/core';
import { AnnouncementService } from '@app/core/services/announcement.service';
import { AttachmentService } from '@app/core/services/attachment.service';
import { GroupService } from '@app/core/services/group.service';
import { PortalService } from '@app/core/services/portal.service';
import { SHARED_MODULES } from '@app/shared';
import { TextEditorComponent } from '@app/shared/modules/text-editor/text-editor.component';
import { currentAcademicPeriod, notNull } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { AttachmentDropzoneComponent, ExistingAttachment } from '../attachment-dropzone/attachment-dropzone.component';
import { UploadFile } from '../attachment-dropzone/dropzone.types';

@Component({
  imports: [SHARED_MODULES, TextEditorComponent, AttachmentDropzoneComponent],
  templateUrl: './modal-announcement-new.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAnnouncementNewComponent implements OnInit, OnDestroy {
  public readonly dialog = inject(Dialog);
  public readonly dialogRef = inject(DialogRef<boolean>);
  public readonly data = inject(DIALOG_DATA);

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _alertService = inject(AlertService);
  private readonly _portalService = inject(PortalService);
  private readonly _groupService = inject(GroupService);
  private readonly _studentService = inject(StudentService);
  private readonly _translateService = inject(TranslateService);

  private readonly _notificationService = inject(NotificationService);
  private readonly _announcementService = inject(AnnouncementService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _store = inject(Store<AppState>);
  private readonly _destroy = inject(DestroyRef);
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=communications`;

  public form: FormGroup;
  public group$: Observable<any[]>;
  public allGroups$: Observable<any[]>;
  public schoolGroups$: Observable<any[]>;
  public readonly students$: Observable<any[]>;
  private readonly _students$ = new BehaviorSubject<any[]>([]);
  private readonly _loadStudents$ = new Subject<{ groupIds: number[]; preselected: any[] }>();
  public disable: boolean;
  public loading = signal(false);
  public today = new Date();
  public academicPeriod$: Observable<{ start: Date; end: Date }>;

  constructor() {
    this.students$ = this._students$.asObservable();
  }

  private get attachments(): FormControl {
    return this.form.get('attachments') as FormControl;
  }

  public get existingAttachments(): ExistingAttachment[] {
    const attachments = this.attachments.value ?? [];

    return attachments
      .filter((att: any) => att.id !== null)
      .map((att: any) => ({
        id: att.id,
        name: att.title ?? att.file,
        size: att.size ?? 0,
        url: att.url,
        mimeType: att.mime_type,
      }));
  }

  public ngOnInit() {
    this.initForm();
    this.loadGroups();
    if (this.data?.announcement) {
      const announcement = this.data.announcement;
      const groupingIds = (announcement.communication_groupings || []).map((grouping) => grouping.grouping_id);

      if (!!announcement.attachments?.length) {
        this.attachments.setValue(
          announcement.attachments.map((a) => ({
            id: a.id,
            name: a.title ?? a.file,
            size: a.size ?? 0,
            url: a.url,
            mimeType: a.mime_type,
          })),
        );
      }

      this.form.patchValue({
        ...announcement,
        groupings: groupingIds,
      });

      const targetUsers = this.normalizeTargetUsers(announcement);
      if (targetUsers.length) {
        const groupIds = [...new Set(targetUsers.map((user) => user.group_id).filter((id) => !!id))];

        this.form.patchValue({ specificStudents: true });
        this.form.patchValue(
          {
            users: targetUsers,
            groupFilter: groupIds,
          },
          { emitEvent: false },
        );

        if (groupIds.length) {
          this.loadStudents(groupIds, targetUsers);
        }
      }
    }
  }

  public ngOnDestroy(): void {
    this._students$.complete();
    this._loadStudents$.complete();
  }

  public onSave() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      if (!this.form.value.description && !this.form.value.attachments?.length) {
        this._alertService.alert({ title: 'status.atencao', message: 'comunicados.descricao-ou-anexo-obrigatorio' });
        return;
      }

      this.save();
    }
  }

  protected onUploaded(uploadFileEvent: UploadFile) {
    if (!uploadFileEvent) {
      return;
    }
    const file = {
      id: null,
      dropzoneId: uploadFileEvent.id,
      file: uploadFileEvent.uploadedFile,
      title: uploadFileEvent.name,
      size: uploadFileEvent.size,
      extension: uploadFileEvent.name.split('.').pop()?.toLowerCase(),
      key: uploadFileEvent.key,
      url: null,
    };
    this.attachments.setValue([...(this.attachments.value || []), file]);
    this.attachments.markAsDirty();
    this.attachments.markAsTouched();
  }

  public onFilesChanged(files: UploadFile[]) {
    const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'waiting');
    this.disable = isUploading;
    this._cdRef.markForCheck();
  }

  public onFileRemoved(targetId: string) {
    const targetFile = this.attachments.value.find(
      (att: any) => String(att.id) === targetId || att.dropzoneId === targetId,
    );

    const updateForm = () => {
      const files = this.attachments.value.filter(
        (att: any) => String(att.id) !== targetId && att.dropzoneId !== targetId,
      );
      this.attachments.setValue(files);
    };

    if (targetFile?.id) {
      this._attachmentService.deleteAttachment(targetFile.id).subscribe((res) => {
        if (res.ret) {
          this._alertService.success({ message: 'geral.excluido-sucesso' });
        } else {
          this._alertService.alert({ message: res.msg || 'geral.excluido-erro' });
        }
        updateForm();
      });
    } else {
      updateForm();
    }
  }

  public icon(extension): string {
    return '/assets/images/files/' + extension + '.svg';
  }

  public roundedNumber(numero: number): number {
    return numero > 1000 ? Math.round(numero / 1000) : Math.round(numero);
  }

  public onCancel() {
    this.form.reset();
    this.dialogRef.close();
  }

  public getSelectedGroups(groups: any[]): any[] {
    const selectedIds = this.form.get('groupings').value || [];
    return groups.filter((group) => selectedIds.includes(group.id));
  }

  public onRemoveGroup(id: any): void {
    const selectedIds = this.form.get('groupings').value || [];
    const newIds = selectedIds.filter((selectedId) => selectedId !== id);
    this.form.get('groupings').setValue(newIds);
    this._cdRef.markForCheck();
  }

  public onRemoveStudent(id: any): void {
    const selectedStudents = this.form.get('users').value || [];
    const newStudents = selectedStudents.filter((student) => student.user_id !== id);
    this.form.get('users').setValue(newStudents);
    this._cdRef.markForCheck();
  }

  public compareStudents = (a: any, b: any): boolean => a?.user_id === b?.user_id;

  private normalizeTargetUsers(announcement: any): any[] {
    const raw = announcement?.communication_users ?? announcement?.users ?? [];

    return raw
      .map((user: any) => {
        const data = typeof user === 'object' && user !== null ? user : {};
        const user_id = Number(data.user_id);
        if (!user_id) {
          return undefined;
        }

        const name = [data.user?.name ?? data.name ?? data.nome, data.user?.sobrenome]
          .filter((part) => !!part)
          .join(' ');

        return {
          user_id,
          group_id: data.group_id !== undefined ? Number(data.group_id) : undefined,
          name,
        };
      })
      .filter((user) => !!user);
  }

  private initForm() {
    this.form = this._formBuilder.group({
      id: undefined,
      entity_id: undefined,
      title: [undefined, Validators.required],
      description: undefined,
      groupings: [undefined, Validators.required],
      code: undefined,
      author: undefined,
      attachments: [[]],
      date: [this.today, Validators.required],
      specificStudents: false,
      users: [[]],
      groupFilter: [[]],
    });

    this.form
      .get('specificStudents')
      .valueChanges.pipe(takeUntilDestroyed(this._destroy))
      .subscribe((specificStudents) => {
        const groupings = this.form.get('groupings');
        const students = this.form.get('users');

        if (specificStudents) {
          groupings.clearValidators();
          groupings.setValue(undefined);
          students.setValidators(Validators.required);
        } else {
          groupings.setValidators(Validators.required);
          students.clearValidators();
          students.setValue([]);
        }

        groupings.updateValueAndValidity();
        students.updateValueAndValidity();
      });

    this.form
      .get('groupFilter')
      .valueChanges.pipe(takeUntilDestroyed(this._destroy))
      .subscribe((groupIds) => {
        this.loadStudents(groupIds ?? [], this.form.get('users').value || []);
      });

    this._loadStudents$
      .pipe(
        takeUntilDestroyed(this._destroy),
        switchMap(({ groupIds, preselected }) => this.fetchStudents(groupIds, preselected)),
      )
      .subscribe((list) => this._students$.next(list));
  }

  private loadGroups() {
    const activeUser$ = this._store.select(AppSelectors.ActiveUser).pipe(
      takeUntilDestroyed(this._destroy),
      notNull(),
      tap(({ entidade_id }) => this.form.get('entity_id').setValue(entidade_id)),
      shareReplay(1),
    );

    this.allGroups$ = activeUser$.pipe(
      switchMap(({ entidade_id }) => this._portalService.getUserGroups(entidade_id)),
      map((res: any) => res?.data || res || []),
      shareReplay(1),
    );

    this.schoolGroups$ = activeUser$.pipe(
      switchMap(({ entidade_id }) => this._groupService.list({ entity_id: entidade_id })),
      map((res: any) => res?.data || res || []),
      shareReplay(1),
    );

    this.group$ = combineLatest([
      this.allGroups$,
      this.form
        .get('groupings')
        .valueChanges.pipe(takeUntilDestroyed(this._destroy), startWith(this.form.get('groupings').value)),
    ]).pipe(
      map(([groups, selectedIds]) => {
        const ids = Array.isArray(selectedIds) ? selectedIds : [];
        return (groups || []).filter((group) => !ids.includes(group.id));
      }),
    );

    this.academicPeriod$ = activeUser$.pipe(map((activeUser) => currentAcademicPeriod(activeUser)));
  }

  private loadStudents(groupIds: number[], preselected: any[] = []) {
    this._loadStudents$.next({ groupIds, preselected });
  }

  private fetchStudents(groupIds: number[], preselected: any[]): Observable<any[]> {
    const entityId = this.form.get('entity_id')?.value;
    const ids = [...new Set((groupIds || []).filter((id) => !!id))];

    if (!entityId || !ids.length) {
      return of(preselected);
    }

    return combineLatest(
      ids.map((groupId) => this._studentService.getStudentsByEntity(entityId, { group_id: groupId })),
    ).pipe(
      map((responses: any[]) => {
        const list: any[] = [...preselected];
        responses.forEach((res, index) => {
          const groupStudents: any[] = res?.data || res || [];
          for (const student of groupStudents) {
            const studentId = student.user_id ?? student.id;
            if (!list.some((item) => (item.user_id ?? item.id) === studentId)) {
              list.push({ ...student, group_id: ids[index] });
            }
          }
        });
        return list;
      }),
    );
  }

  private save() {
    this._alertService
      .confirm({
        message: !!this.form.value.id
          ? this._translateService.instant(`comunicados.alterar-comunicado`)
          : this._translateService.instant(`comunicados.salvar-comunicado`),
      })
      .pipe(
        filter((result) => !!result),
        tap(() => this.loading.set(true)),
        map(() => {
          const value = this.form.value;
          const specificStudents = !!value.specificStudents;

          return {
            ...value,
            groupings: specificStudents ? undefined : value.groupings,
            users: specificStudents
              ? (value.users || []).map((student) => ({ user_id: student.user_id, group_id: student.group_id }))
              : undefined,
            description: !isEmpty(value.description) ? value.description : undefined,
            attachments: !isEmpty(value.attachments) ? value.attachments : undefined,
          };
        }),
        switchMap((value) =>
          (!!value.id
            ? this._announcementService.updateAnnoucement(value.id, value)
            : this._announcementService.saveAnnoucement({
                ...value,
                attachments: value.attachments?.map(({ id, ...attachment }) => attachment),
              })
          ).pipe(
            catchError(() => {
              this.loading.set(false);
              return of({ ret: 0 });
            }),
          ),
        ),
        tap(() => this.loading.set(false)),
      )
      .subscribe((res) => {
        if (res.ret === 1) {
          this._notificationService.success('geral.sucesso');
          this.dialogRef.close(true);
        } else {
          this._notificationService.error('comunicados.excluido-erro');
        }
      });
  }
}
